/* eslint-disable react-refresh/only-export-components */
import React, { useCallback, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Divider,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import {
    CheckCircleOutline as ResolvedIcon,
    PhoneInTalk as TechLineIcon,
    Inventory2 as RmaIcon,
    VerifiedUser as WarrantyIcon,
    ContentCopy as CopyIcon,
    Check as CheckIcon,
    ArrowBack as BackIcon,
    Close as ExitIcon,
    NavigateNext as NextIcon,
    ReportProblem as CautionIcon,
    ErrorOutline as StopIsolateIcon,
    AssignmentTurnedIn as FlowIcon,
    HelpOutline as HelpIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import type {
    CitedSource,
    DocUrlResponse,
    FlowAnswerPayload,
    FlowNode,
    FlowRun,
    FlowSuggestedAction,
    FlowSuggestedActionCode,
    FlowSuggestion,
    FlowTerminal,
    FlowTerminalKind,
} from './types';
import { SourceChips } from './SourceChips';

// ── 1. Suggestion / Offer Card ───────────────────────────────────────────────

export interface FlowSuggestionCardProps {
    suggestion: FlowSuggestion;
    disabled?: boolean;
    onStart: (flowId: string) => void | Promise<unknown>;
    onDismiss: (flowId: string) => void | Promise<unknown>;
}

export const FlowSuggestionCard: React.FC<FlowSuggestionCardProps> = ({
    suggestion,
    disabled = false,
    onStart,
    onDismiss,
}) => {
    const theme = useTheme();

    return (
        <Card
            data-testid="troubleshooting-flow-card"
            variant="outlined"
            sx={{
                my: 1.5,
                borderRadius: 2,
                borderColor: theme.palette.secondary.main,
                bgcolor: alpha(theme.palette.secondary.main, 0.04),
                boxShadow: 1,
            }}
        >
            <CardContent sx={{ pb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <FlowIcon color="secondary" sx={{ mt: 0.25 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'secondary.main', letterSpacing: 0.5 }}>
                            Troubleshooting Flow Available
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.25 }}>
                            {suggestion.title}
                        </Typography>
                        {suggestion.reason && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {suggestion.reason}
                            </Typography>
                        )}
                        {suggestion.error_codes && suggestion.error_codes.length > 0 && (
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                                {suggestion.error_codes.map((code) => (
                                    <Chip key={code} label={`Error ${code}`} size="small" variant="outlined" color="secondary" />
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 1.5, pt: 0, justifyContent: 'flex-end', gap: 1 }}>
                <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    disabled={disabled}
                    onClick={() => void onDismiss(suggestion.flow_id)}
                >
                    Dismiss
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    disabled={disabled}
                    endIcon={<NextIcon />}
                    onClick={() => void onStart(suggestion.flow_id)}
                >
                    Start flow
                </Button>
            </CardActions>
        </Card>
    );
};

// ── 2. Flow Step Card ────────────────────────────────────────────────────────

export interface FlowStepCardProps {
    node: FlowNode;
    flowTitle?: string | null;
    sessionId?: string | null;
    needsClarification?: boolean;
    isSubmitting?: boolean;
    onAnswer: (payload: FlowAnswerPayload) => void | Promise<unknown>;
    onBack: () => void | Promise<unknown>;
    onExit: () => void | Promise<unknown>;
    onFetchDocUrl?: (documentId: string, pageNumber?: number) => Promise<DocUrlResponse>;
}

export const FlowStepCard: React.FC<FlowStepCardProps> = ({
    node,
    flowTitle,
    sessionId,
    needsClarification = false,
    isSubmitting = false,
    onAnswer,
    onBack,
    onExit,
    onFetchDocUrl,
}) => {
    const theme = useTheme();
    const [readingValue, setReadingValue] = useState('');
    const [readingError, setReadingError] = useState<string | null>(null);

    const isStopIsolate = Boolean(node.stop_banner || node.safety_level === 'STOP_ISOLATE');
    const isCaution = node.safety_level === 'CAUTION' && !isStopIsolate;

    const citedSources: CitedSource[] = useMemo(() => {
        if (!node.citation) return [];
        return [
            {
                ref: '1',
                document_id: node.citation.document_id,
                page_number: node.citation.page_number,
                printed_page_label: node.citation.printed_page_label ?? null,
                clause_ref: null,
                layer: 'MANUFACTURER',
                title: node.citation.title ?? 'Reference Document',
                display_policy: 'FULL_LINK',
                link: true,
            },
        ];
    }, [node.citation]);

    const handleSubmitReading = () => {
        const trimmed = readingValue.trim();
        const parsed = Number(trimmed);
        if (!trimmed || Number.isNaN(parsed)) {
            setReadingError('Enter a valid numeric reading.');
            return;
        }
        setReadingError(null);
        void onAnswer({
            node_id: node.node_id,
            reading: {
                value: parsed,
                ...(node.input.unit ? { unit: node.input.unit } : {}),
            },
        });
    };

    const hasOptions = Array.isArray(node.options) && node.options.length > 0;
    const showOptions = node.input.type === 'OPTIONS' || (node.input.type === 'PHOTO' && hasOptions);

    return (
        <Card variant="outlined" sx={{ my: 1.5, borderRadius: 2, borderColor: theme.palette.divider }}>
            <CardContent>
                {/* Header Row: Title, Step Progress, Back, Exit */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'secondary.main' }}>
                            {flowTitle ? `GUIDED CHECK · ${flowTitle.toUpperCase()}` : 'GUIDED CHECK'}
                        </Typography>
                        {node.progress && (
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Step {node.progress.step} of ~{node.progress.of_estimate}
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<BackIcon fontSize="small" />}
                            disabled={isSubmitting}
                            onClick={() => void onBack()}
                        >
                            Back
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<ExitIcon fontSize="small" />}
                            disabled={isSubmitting}
                            onClick={() => void onExit()}
                        >
                            Exit
                        </Button>
                    </Stack>
                </Stack>

                {/* STOP_ISOLATE Banner */}
                {isStopIsolate && (
                    <Alert
                        severity="error"
                        icon={<StopIsolateIcon />}
                        sx={{ mb: 1.5, fontWeight: 600 }}
                    >
                        STOP — Isolate before continuing. Follow lock-out/tag-out procedures.
                    </Alert>
                )}

                {/* CAUTION Banner */}
                {isCaution && (
                    <Alert
                        severity="warning"
                        icon={<CautionIcon />}
                        sx={{ mb: 1.5, fontWeight: 600 }}
                    >
                        Caution — verify site conditions and PPE before testing.
                    </Alert>
                )}

                {/* Clarification Banner */}
                {needsClarification && (
                    <Alert
                        severity="info"
                        icon={<HelpIcon />}
                        sx={{ mb: 1.5 }}
                    >
                        Please double-check or clarify your response for this step.
                    </Alert>
                )}

                {/* Instruction Markdown */}
                <Box sx={{ my: 1, '& p': { my: 0.5 }, '& ul, & ol': { pl: 2.5 } }}>
                    <ReactMarkdown disallowedElements={['img']} unwrapDisallowed>
                        {node.instruction_md}
                    </ReactMarkdown>
                </Box>

                {/* Citation Chips */}
                {citedSources.length > 0 && (
                    <SourceChips
                        sources={citedSources}
                        sessionId={sessionId}
                        onFetchDocUrl={onFetchDocUrl}
                    />
                )}

                {/* Step Input Controls */}
                <Box sx={{ mt: 2 }}>
                    {showOptions && (
                        <Stack spacing={1}>
                            {(node.options ?? []).map((opt) => (
                                <Button
                                    key={opt.id}
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isSubmitting}
                                    onClick={() => void onAnswer({ node_id: node.node_id, option_id: opt.id })}
                                    endIcon={<NextIcon />}
                                    sx={{
                                        justifyContent: 'space-between',
                                        textAlign: 'left',
                                        textTransform: 'none',
                                        py: 1,
                                        px: 2,
                                        fontWeight: 600,
                                    }}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </Stack>
                    )}

                    {node.input.type === 'READING' && (
                        <Box sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <TextField
                                    size="small"
                                    label="Measured reading"
                                    value={readingValue}
                                    onChange={(e) => setReadingValue(e.target.value)}
                                    error={Boolean(readingError)}
                                    helperText={readingError}
                                    disabled={isSubmitting}
                                    InputProps={{
                                        endAdornment: node.input.unit ? (
                                            <InputAdornment position="end">{node.input.unit}</InputAdornment>
                                        ) : undefined,
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSubmitReading();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    disabled={isSubmitting || !readingValue.trim()}
                                    onClick={handleSubmitReading}
                                    sx={{ height: 40 }}
                                >
                                    Submit
                                </Button>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

// ── 3. Flow Terminal Card ────────────────────────────────────────────────────

export interface FlowTerminalCardProps {
    terminal: FlowTerminal;
    isSubmitting?: boolean;
    onBack: () => void | Promise<unknown>;
    onExit: () => void | Promise<unknown>;
    onDismiss?: () => void | Promise<unknown>;
    onFetchSummary: () => Promise<string | null>;
    onSelectSuggestedAction?: (actionCode: FlowSuggestedActionCode | string) => void;
}

const TERMINAL_META: Record<
    FlowTerminalKind,
    { label: string; icon: React.ReactNode; color: 'success' | 'info' | 'warning' | 'error' }
> = {
    RESOLVED: { label: 'Issue Resolved', icon: <ResolvedIcon />, color: 'success' },
    TECH_LINE: { label: 'Escalate to Manufacturer Tech Line', icon: <TechLineIcon />, color: 'info' },
    RMA: { label: 'Prepare RMA Pack', icon: <RmaIcon />, color: 'info' },
    WARRANTY: { label: 'Lodge Warranty Claim', icon: <WarrantyIcon />, color: 'warning' },
    FOLLOW_ON: { label: 'Create Follow-On Job', icon: <FlowIcon />, color: 'info' },
    BOOK_PROFESSIONAL: { label: 'Specialist Attendance Required', icon: <CautionIcon />, color: 'warning' },
    HAZARD_STOP: { label: 'HAZARD — Stop Work & Isolate', icon: <StopIsolateIcon />, color: 'error' },
};

export const SUGGESTED_ACTION_LABELS: Record<FlowSuggestedActionCode, string> = {
    CLOSE_RESOLVED: 'Close & Mark Resolved',
    TECH_LINE_SUMMARY: 'Tech Line Summary',
    CALL_TECH_SUPPORT: 'Call Tech Support',
    FLOW_SUMMARY: 'Copy Flow Summary',
    OPEN_RMA: 'Open RMA Pack',
    RAISE_WARRANTY_CLAIM: 'Raise Warranty Claim',
    CREATE_FOLLOW_ON_JOB: 'Create Follow-On Job',
    BOOK_PROFESSIONAL: 'Book Licensed Specialist',
    ISOLATE_AND_STOP: 'Isolate & Stop Work',
};

export function normalizeSuggestedAction(
    item: FlowSuggestedAction | null | undefined,
): { key: FlowSuggestedActionCode; label: string } | null {
    if (!item) return null;
    const rawCode =
        typeof item === 'string'
            ? item
            : (item.code ?? item.action ?? item.type ?? item.id ?? '');
    const code = rawCode.trim().toUpperCase() as FlowSuggestedActionCode;
    if (!Object.prototype.hasOwnProperty.call(SUGGESTED_ACTION_LABELS, code)) {
        return null;
    }
    return {
        key: code,
        label: SUGGESTED_ACTION_LABELS[code],
    };
}

export const FlowTerminalCard: React.FC<FlowTerminalCardProps> = ({
    terminal,
    isSubmitting = false,
    onBack,
    onExit,
    onDismiss,
    onFetchSummary,
    onSelectSuggestedAction,
}) => {
    const [copied, setCopied] = useState(false);
    const [copying, setCopying] = useState(false);
    const [copyError, setCopyError] = useState<string | null>(null);

    const terminalKindKey = (terminal.terminal ?? terminal.terminal_kind ?? '') as FlowTerminalKind;
    const meta = Object.prototype.hasOwnProperty.call(TERMINAL_META, terminalKindKey)
        ? TERMINAL_META[terminalKindKey]
        : { label: 'Troubleshooting Complete', icon: <ResolvedIcon />, color: 'info' as const };
    const showStopBanner = Boolean(terminal.stop_banner || terminalKindKey === 'HAZARD_STOP');

    const handleCopySummary = useCallback(async () => {
        setCopying(true);
        setCopyError(null);
        try {
            const summaryMd = await onFetchSummary();
            const textToCopy = summaryMd || terminal.message_md;
            if (!textToCopy || !navigator.clipboard?.writeText) {
                setCopyError('Could not copy summary to clipboard.');
                return;
            }
            await navigator.clipboard.writeText(textToCopy);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (err: unknown) {
            const msg =
                err instanceof Error && err.message
                    ? err.message
                    : 'Could not copy summary to clipboard.';
            setCopyError(msg);
        } finally {
            setCopying(false);
        }
    }, [onFetchSummary, terminal.message_md]);

    const handleActionPress = useCallback(
        (code: FlowSuggestedActionCode) => {
            if (code === 'FLOW_SUMMARY') {
                void handleCopySummary();
            }
            if (code === 'CLOSE_RESOLVED') {
                if (onDismiss) void onDismiss();
                else void onExit();
            }
            onSelectSuggestedAction?.(code);
        },
        [handleCopySummary, onDismiss, onExit, onSelectSuggestedAction],
    );

    const actions = useMemo(
        () =>
            (terminal.suggested_actions ?? [])
                .map((item) => normalizeSuggestedAction(item))
                .filter((a): a is { key: FlowSuggestedActionCode; label: string } => a !== null),
        [terminal.suggested_actions],
    );

    return (
        <Card variant="outlined" sx={{ my: 1.5, borderRadius: 2 }}>
            <CardContent>
                <Alert severity={meta.color} icon={meta.icon} sx={{ mb: 1.5, fontWeight: 700 }}>
                    {meta.label}
                </Alert>

                {showStopBanner && (
                    <Alert severity="error" icon={<StopIsolateIcon />} sx={{ mb: 1.5, fontWeight: 700 }}>
                        STOP — Isolate before continuing
                    </Alert>
                )}

                <Box sx={{ my: 1, '& p': { my: 0.5 }, '& ul, & ol': { pl: 2.5 } }}>
                    <ReactMarkdown disallowedElements={['img']} unwrapDisallowed>
                        {terminal.message_md}
                    </ReactMarkdown>
                </Box>

                {copyError && (
                    <Alert
                        severity="error"
                        sx={{ mt: 1, py: 0.25, px: 1, fontSize: '0.8rem' }}
                        onClose={() => setCopyError(null)}
                    >
                        {copyError}
                    </Alert>
                )}

                {/* Suggested Actions */}
                {actions.length > 0 && (
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        {actions.map((act) => (
                            <Button
                                key={act.key}
                                variant="contained"
                                color="secondary"
                                onClick={() => handleActionPress(act.key)}
                                endIcon={<NextIcon />}
                                sx={{ justifyContent: 'space-between', textTransform: 'none' }}
                            >
                                {act.label}
                            </Button>
                        ))}
                    </Stack>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Footer Actions */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={copied ? <CheckIcon color="success" /> : <CopyIcon />}
                        disabled={copying}
                        onClick={() => void handleCopySummary()}
                    >
                        {copied ? 'Copied!' : copying ? 'Copying…' : 'Copy summary'}
                    </Button>
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<BackIcon />}
                            disabled={isSubmitting}
                            onClick={() => void onBack()}
                        >
                            Back
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                            onClick={() => (onDismiss ? void onDismiss() : void onExit())}
                        >
                            Done
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

// ── 4. Unified Troubleshooting Flow Card Container ──────────────────────────

export interface TroubleshootingFlowCardProps {
    offer?: FlowSuggestion | null;
    activeRun?: FlowRun | null;
    activeNode?: FlowNode | null;
    activeTerminal?: FlowTerminal | null;
    flowTitle?: string | null;
    sessionId?: string | null;
    isSubmitting?: boolean;
    needsClarification?: boolean;
    onStart: (flowId: string) => void | Promise<unknown>;
    onDismiss: (flowId?: string) => void | Promise<unknown>;
    onAnswer: (payload: FlowAnswerPayload) => void | Promise<unknown>;
    onBack: () => void | Promise<unknown>;
    onExit: () => void | Promise<unknown>;
    onFetchDocUrl?: (documentId: string, pageNumber?: number) => Promise<DocUrlResponse>;
    onFetchSummary: () => Promise<string | null>;
    onSelectSuggestedAction?: (actionCode: FlowSuggestedActionCode | string) => void;
}

export const TroubleshootingFlowCard: React.FC<TroubleshootingFlowCardProps> = ({
    offer,
    activeRun,
    activeNode,
    activeTerminal,
    flowTitle,
    sessionId,
    isSubmitting = false,
    needsClarification = false,
    onStart,
    onDismiss,
    onAnswer,
    onBack,
    onExit,
    onFetchDocUrl,
    onFetchSummary,
    onSelectSuggestedAction,
}) => {
    if (activeTerminal) {
        return (
            <FlowTerminalCard
                terminal={activeTerminal}
                isSubmitting={isSubmitting}
                onBack={onBack}
                onExit={onExit}
                onDismiss={() => {
                    if (onDismiss) void onDismiss(activeRun?.flow_id);
                    else void onExit();
                }}
                onFetchSummary={onFetchSummary}
                onSelectSuggestedAction={onSelectSuggestedAction}
            />
        );
    }

    if (activeNode) {
        return (
            <FlowStepCard
                node={activeNode}
                flowTitle={flowTitle || activeRun?.flow_title}
                sessionId={sessionId}
                needsClarification={needsClarification}
                isSubmitting={isSubmitting}
                onAnswer={onAnswer}
                onBack={onBack}
                onExit={onExit}
                onFetchDocUrl={onFetchDocUrl}
            />
        );
    }

    if (offer) {
        return (
            <FlowSuggestionCard
                suggestion={offer}
                disabled={isSubmitting}
                onStart={onStart}
                onDismiss={onDismiss}
            />
        );
    }

    return null;
};

export default TroubleshootingFlowCard;
